<template>
    <b-form @submit.prevent="saveForm" :class="'h-100 d-flex'">
        <b-form-row class="w-100 mx-0">
            <b-col cols="12" sm="4" class="px-0">
                <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' }, className : 'os-theme-dark os-theme-gekko' }">
                    <div class="modal-form-legend">
                        <legend v-translate>Document info</legend>
                    </div>

                    <b-form-group id="input-group-2" label-class="gk-label-margin gk-modal-label" label-for="input-2">
                        <template slot="label">
                            <span v-translate>Title*</span>
                        </template>
                        <b-input-group>
                            <b-form-input id="input-2" type="text" v-model="documentDetails.title" :required="true"/>
                        </b-input-group>
                    </b-form-group>
                
                    <b-form-group id="input-group-3" label-class="gk-label-margin gk-modal-label" label-for="input-3">
                        <template slot="label">
                            <span v-translate>Description</span>
                        </template>
                        <b-input-group>
                            <b-form-input id="input-3" type="text" v-model="documentDetails.description"/>
                        </b-input-group>
                    </b-form-group>

                    <b-form-group id="input-group-4" label-class="gk-label-margin gk-modal-label" label-for="input-4">
                        <template slot="label">
                            <span v-translate>Type</span>
                        </template>
                        <b-input-group>
                            <b-form-select id="input-4" v-model="documentDetails.type" :options="typeOptions"></b-form-select>
                        </b-input-group>
                    </b-form-group>

                    <b-form-row>
                        <b-col cols="12" lg="12">
                            <b-form-group id="input-group-5" label-class="gk-label-margin gk-modal-label" label-for="input-5">
                                <template slot="label">
                                    <span v-translate>Valid From</span>
                                </template>
                                <b-input-group>
                                    <b-form-datepicker id="input-5" v-model="documentDetails.valid_from" :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"></b-form-datepicker>
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                    </b-form-row>

                    <b-form-row>
                        <b-col cols="12" lg="12">
                            <b-form-group id="input-group-6" label-class="gk-label-margin gk-modal-label" label-for="input-6">
                                <template slot="label">
                                    <span v-translate>Valid Until</span>
                                </template>
                                <b-input-group>
                                    <b-form-datepicker id="input-6" v-model="documentDetails.valid_until" :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"></b-form-datepicker>
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                    </b-form-row>

                    <div class="modal-form-legend">
                        <legend v-translate>Customer & Project</legend>
                    </div>

                    <b-form-row>
                        <b-col cols="12" lg="6">
                            <b-form-group id="input-group-7" label-class="gk-label-margin gk-modal-label" label-for="input-7">
                                <template slot="label">
                                    <span v-translate>Customer</span>
                                </template>
                                <CustomerSelector v-model="documentDetails.customer" @change="customerCallback" :filters="{owner_company: getCompanyId, is_archived: false}" />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" lg="6">
                            <b-form-group id="input-group-8" label-class="gk-label-margin gk-modal-label" label-for="input-8">
                                <template slot="label">
                                    <span v-translate>Project</span>
                                </template>
                                <b-input-group>
                                    <b-form-select id="input-8" v-model="documentDetails.project" :options="projectOptions" :disabled="projectOptions.length===0"></b-form-select>
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                    </b-form-row>
                    
                </overlay-scrollbars>
            </b-col>

            <b-col cols="12" sm="8" class="modal-dropzone-actions pr-0">
                <div class="modal-dropzone-actions--wrap">
                    <div class="modal-dropzone-wrapper">
                        <VueDropzone :preloaded_file="imageSrc" :allowed-files="'all'" :onlyChange="true" @change="fileChanged" />
                    </div>
                </div>
            </b-col>

        </b-form-row>
    </b-form>
</template>

<script>
import { mapGetters } from 'vuex';

import VueDropzone from "@/components/VueDropzone";
import CustomerSelector from "@/components/CustomerSelector";
import CustomerService from "@/store/services/customer.service";
import DocumentService from "@/store/services/document.service";

export default {
    name: "AddEditDocument",
    components: {CustomerSelector, VueDropzone},
    data() {
        return {
            documentDetails: {
                file: null,
                title: "",
                description: "",
                customer: null,
                project: null,
                type: null,
                valid_from: null,
                valid_until: null
            },
            typeOptions: [
                {value: "contract", text: this.$gettext('Contract')},
                {value: "id", text: this.$gettext('Id')},
                {value: "diploma", text: this.$gettext('Diploma')},
                {value: "terms", text: this.$gettext('Terms')},
                {value: "cv", text: this.$gettext('CV')},
                {value: "presentation", text: this.$gettext('Presentation')},
                {value: "others", text: this.$gettext('Others')}
            ],
            selectedCustomer: null,
            projectOptions: [],
            imageSrc: null
        }
    },
    computed: {
        ...mapGetters({ 
            getCompanyId: 'main/getCompanyId'
        }),
    },
    async created() {
        if (this.$route.params['documentId']) {
            await this.retrieveDocument();
            if (this.documentDetails.customer)
                await this.customerCallback(this.documentDetails.customer.id);
        }
    },
    methods: {
        async retrieveDocument() {
            return DocumentService.retrieve(this.$route.params['documentId']).then((response) => {
                this.documentDetails = response.data;
                this.imageSrc = response.data.file;
                this.$emit("documentDetails", this.documentDetails);
            });
        },
        async saveForm() {
            this.documentDetails.company_profile = this.getCompanyId;

            if (!this.$route.params['documentId']) {
                DocumentService.create(this.documentDetails).then((response) => {
                    this.$emit("created", response.data);
                    this.$router.push({name: "Documents"});
                    this.$store.commit('setAlert', {
                        title: this.$gettext("Document added"),
                        text: this.$gettext("The document was added to the list."),
                        variant: "success",
                        duration: 4
                    });
                }).catch((error) => {
                    if (error.status === 499) {
                      console.log(error);
                        this.$store.commit('setAlert', {
                            title: this.$gettext("Error"),
                            text: error.data.detail,
                            variant: "danger",
                            duration: 4
                        });
                        this.$store.commit('togglePremiumModal', 'web_documents_filelimit');
                    } else if (error.status === 400) {
                        if (error.data && error.data.file) {
                            this.$store.commit('setAlert', {
                                title: this.$gettext("Error"),
                                text: this.$gettext("No file was submitted."),
                                variant: "danger",
                                duration: 4
                            });
                        } else if (error.data && error.data.non_field_errors) {
                            this.$store.commit('setAlert', {
                                title: this.$gettext("Error"),
                                text: error.data.non_field_errors,
                                variant: "danger",
                                duration: 4
                            });
                        } else {
                            this.$store.commit('setAlert', {
                                title: this.$gettext("Error"),
                                text: this.$gettext("Error uploading document."),
                                variant: "danger",
                                duration: 4
                            });
                        }
                    } else {
                        this.$store.commit('setAlert', {
                            title: this.$gettext("Error"),
                            text: this.$gettext("Something went wrong..."),
                            variant: "danger",
                            duration: 4
                        });
                    }
                });
            } else {
                if (typeof this.documentDetails['file'] === "string") {
                    DocumentService.getFile(this.documentDetails.id).then((response) => response.blob()).then((response) => {
                        this.documentDetails.file = response;
                        this.documentDetails.file.name = this.documentDetails.filename;
                    }).then(() => {
                        this._updateDocument();
                    });
                } else { 
                    this._updateDocument();
                }
            }
        },
        _updateDocument() {
            DocumentService.update(this.$route.params['documentId'], this.documentDetails).then((response) => {
                let item = response.data;
                if (this.selectedCustomer) {
                    item.customer_name = this.selectedCustomer?.name;
                    let project = this.projectOptions.find((p) => p.value === this.documentDetails.project);
                    if (project)
                        item.project_name = project?.name;
                    else
                        item.project_name = "";
                } else {
                    item.customer_name = "";
                    item.project_name = "";
                }
                
                this.$emit("updated", item);
                this.$router.push({name: "Documents"});
                this.$store.commit('setAlert', {
                    title: this.$gettext("Document edited"),
                    text: this.$gettext("The document was edited correctly."),
                    variant: "success",
                    duration: 4
                });
            }).catch(() => {
                this.$store.commit('setAlert', {
                    title: this.$gettext("Error"),
                    text: this.$gettext("Error editing document."),
                    variant: "danger",
                    duration: 4
                });
            });
        },
        async customerCallback(value) {
            if (!this.documentDetails.customer && !value) {
                this.documentDetails.customer = null;
                this.documentDetails.project = null;
                this.selectedCustomer = null;
                return;
            } else if (this.documentDetails.customer && !value) {
                value = this.documentDetails.customer;
            }

            return CustomerService.retrieve(value, {extended: true}).then((response) => {
                this.selectedCustomer = response.data;

                this.projectOptions = response.data.projects;
                this.projectOptions.unshift({name: this.$gettext('Nothing Selected'), id: null});

                for(var i = 0; i < this.projectOptions.length; i++) {
                    this.projectOptions[i].value = this.projectOptions[i].id;
                    this.projectOptions[i].text = this.projectOptions[i].name;
                }
            });
        },
        fileChanged(file) {
            this.documentDetails.file_size = file.size;
            this.documentDetails.file = file;
            if (!this.$route.params['documentId']) {
                this.documentDetails.title = file.name;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
</style>